// @flow
import React from "react";
import PropTypes from "prop-types";
import { withRouter } from "react-router-dom";
import { connect } from "react-redux";
import { toast } from "react-toastify";
import {
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Label,
  Input,
  FormText,
  Row,
  Col,
  Popover,
  PopoverHeader,
  PopoverBody,
  ButtonGroup
} from "reactstrap";
import "./../../node_modules/react-select/dist/react-select.css";
import Select, { Creatable } from "react-select";
import { bindActionCreators } from "redux";
import * as actions from "../actions";
import { compose, checkForm, setIn } from "../common/utils";
import * as API from "../common/api";
import styled from "../common/styled";
import { s3Delete, s3Move } from "../common/aws_lib";

const { Text, Image, View } = styled.plain;

const checks = [
  {
    related: ["photoName"],
    check: s => s && s.length,
    errMsg: () => "photoName Required"
  },
  {
    related: ["categories"],
    check: s => s && s.length,
    errMsg: () => "categoriesRequired"
  },
  {
    related: ["subcategories"],
    check: s => s && s.length,
    errMsg: () => "subcategories Required"
  },
  {
    related: ["tags"],
    check: s => s && s.length,
    errMsg: () => "tags Required"
  }
];

const checkFormData = checkForm(checks);

class PhotoForm extends React.Component {
  state = {
    errMsg: null,
    isSubmitting: false,
    formData: {
      photoName: "",
      photoAlt: "",
      categories: [],
      subcategories: [],
      tags: []
    },
    popoverOpen: false
  };

  componentDidMount() {
    this.loadData(this.props);
    this.props.loadCategories();
  }

  componentWillReceiveProps(nextProps) {
    if (
      nextProps.type !== this.props.type ||
      nextProps.photoId !== this.props.photoId
    ) {
      this.loadData(nextProps);
    }

    if (nextProps.photo && nextProps.photo !== this.props.photo) {
      this.setState({ formData: nextProps.photo });
    }
  }

  loadData(props) {
    this.props.loadGalleryPhoto(props.photoId);
  }

  toggleDelete = () => {
    this.setState({
      popoverOpen: !this.state.popoverOpen
    });
  };

  onChangeField = (key, val) => {
    const state = setIn(["formData", key], val, this.state);
    this.setState(state);
    if (key === "categories") {
      this.setState(setIn(["formData", "subcategories"], [], state));
    }
  };

  validateForm = fields => {
    const data = this.state.formData;
    const errMsgs = checkFormData(data, fields);

    const newErrMsgs =
      fields && fields.length > 0
        ? { ...this.state.errMsgs, ...errMsgs }
        : errMsgs;
    const hasError =
      Object.keys(newErrMsgs).filter(key => newErrMsgs[key]).length > 0;

    if (!fields && hasError) {
      const firstError = Object.keys(errMsgs)
        .map(key => errMsgs[key])
        .filter(x => !!x)[0];
      toast.error(firstError);
    }

    this.setState({ errMsgs: newErrMsgs });
    return !hasError;
  };

  onSubmit = next => {
    if (!this.validateForm()) return;

    const data = {...this.state.formData};
    const prefix = data.isPrivate ? "private/" : "public/";
    const sourcePrefix = this.props.photo.isPrivate ? "private/" : "public/";
    if (sourcePrefix !== prefix) {
      data.photoWeb = data.photoWeb.replace(`/${sourcePrefix}`, `/${prefix}`);
      data.thumbnail = data.thumbnail.replace(`/${sourcePrefix}`, `/${prefix}`);
      data.photoFull = data.photoFull.replace(`/${sourcePrefix}`, `/${prefix}`);
    }

    return actions.wrapLoading(
      this.props,
      API.UpdatePhoto(
        this.props.photoId,
        data.photoName,
        data.photoNum,
        data.categories,
        data.subcategories,
        data.tags,
        data.isPrivate,
        data.photoWeb,
        data.thumbnail,
        data.photoFull,
        data.photoAlt,
        data.ctaUrl
      ).then(result => {
          toast.success("Successfully Updated");
          if (next) {
            return this.props.onDoneNext();
          }
          return this.props.onDone();
        },
        e => {
          const msg = e.message;
          toast.error(msg);
        }
      )
    );
  };

  onMovePhoto = () => {
    const data = this.state.formData;
    const prefix = data.isPrivate ? "private/" : "public/";
    const sourcePrefix = this.props.photo.isPrivate ? "private/" : "public/";
    if (sourcePrefix === prefix) {
      return Promise.resolve();
    }

    const fileExt = this.state.formData.fileExt || '.jpg'
    const photoKey = this.state.formData.photoKey

    const files = [
      `${photoKey}-full${fileExt}`,
      `${photoKey}-web${fileExt}`,
      `${photoKey}-thumb${fileExt}`
    ];
    const promises = [];

    files.forEach(item => {
      const promise = s3Move(`${prefix}${item}`, `${sourcePrefix}${item}`);
      promises.push(promise);
    });

    return actions.wrapLoading(
      this.props,
      Promise.all(promises).catch(e => {
        console.log(e);
      })
    );
  };

  onDelete = () => {
    const prefix = this.state.formData.isPrivate ? "private/" : "public/";
    const fileExt = this.state.formData.fileExt || '.jpg'
    const photoKey = this.state.formData.photoKey
    const files = [
      `${prefix}${photoKey}-full${fileExt}`,
      `${prefix}${photoKey}-web${fileExt}`,
      `${prefix}${photoKey}-thumb${fileExt}`
    ];

    const promises = [];

    files.forEach(item => {
      const promise = s3Delete(item);
      promises.push(promise);
    });

    return actions.wrapLoading(
      this.props,
      Promise.all(promises)
        .then(values => this.deletePhoto())
        .catch(e => {
          console.log(e);
        })
    );
  };

  deletePhoto = () => {
    API.DeletePhoto(this.props.photoId).then(
      result => {
        toast.success("Successfully Deleted");
        return this.props.onDone();
      },
      e => {
        const msg = e.message;
        toast.error(msg);
      }
    );
  };

  render() {
    const categories = (this.props.categories || []).map(item => ({
      label: item.categoryName,
      value: item.categoryName
    }));
    const foundCategories = (this.props.categories || []).filter(item =>
      this.state.formData.categories.includes(item.categoryName)
    );
    let subcategories = [];
    foundCategories.forEach(cat => {
      const subs = (cat.subcategories || []).map(item => ({
        label: item,
        value: item
      }));
      subcategories = subcategories.concat(subs);
    });
    const tags = this.state.formData.tags.map(item => ({
      label: item,
      value: item
    }));
    console.log(subcategories);
    return (
      <Modal
        isOpen={this.props.visible}
        className="photoModal"
        onClosed={this.props.onClose}
        size="lg"
      >
        <ModalHeader>Manage Photo</ModalHeader>
        <ModalBody>
          <Form className="photo-form">
            <FormGroup>
              <PhotoImage
                style={{
                  backgroundImage: `url(${this.state.formData.photoWeb})`
                }}
              />
            </FormGroup>
            <div className="form-field">
              <Row>
                <Col md="9">
                  <FormGroup>
                    <Label>Name</Label>
                    <Input
                      type="text"
                      name="photoName"
                      required
                      value={this.state.formData.photoName}
                      onChange={e =>
                        this.onChangeField("photoName", e.target.value)
                      }
                    />
                  </FormGroup>
                </Col>
                <Col md="3">
                  <FormGroup>
                    <Label>Number</Label>
                    <Input
                      type="text"
                      name="photoNum"
                      required
                      value={this.state.formData.photoNum}
                      onChange={e =>
                        this.onChangeField("photoNum", e.target.value)
                      }
                    />
                  </FormGroup>
                </Col>
              </Row>

              <Row>
                <Col md="12">
                  <FormGroup>
                    <Label>Categories</Label>
                    <Select
                      multi
                      name="categories"
                      required
                      value={this.state.formData.categories}
                      onChange={e =>
                        this.onChangeField(
                          "categories",
                          e.map(item => item.value)
                        )
                      }
                      options={categories}
                    />
                  </FormGroup>
                </Col>
              </Row>

              <Row>
                <Col md="12">
                  <FormGroup>
                    <Label>Subcategories</Label>
                    <Select
                      multi
                      name="subcategories"
                      required
                      value={this.state.formData.subcategories}
                      onChange={e =>
                        this.onChangeField(
                          "subcategories",
                          e.map(item => item.value)
                        )
                      }
                      options={subcategories}
                    />
                  </FormGroup>
                </Col>
              </Row>

              <Row>
                <Col md="12">
                  <FormGroup>
                    <Label>Tags</Label>
                    <Creatable
                      multi
                      name="tags"
                      required
                      value={this.state.formData.tags}
                      onChange={e =>
                        this.onChangeField(
                          "tags",
                          e.map(item => item.value)
                        )
                      }
                      options={tags}
                    />
                  </FormGroup>
                </Col>
              </Row>

              <Row>
                <Col md="12">
                  <FormGroup>
                    <Label>Alt Text</Label>
                    <Input
                      type="text"
                      name="photoAlt"
                      value={this.state.formData.photoAlt}
                      onChange={e =>
                        this.onChangeField("photoAlt", e.target.value)
                      }
                    />
                  </FormGroup>
                </Col>
              </Row>

              <Row>
                <Col md="12">
                  <FormGroup>
                    <Label>CTA Url</Label>
                    <Input
                      type="text"
                      name="ctaUrl"
                      value={this.state.formData.ctaUrl}
                      onChange={e =>
                        this.onChangeField("ctaUrl", e.target.value)
                      }
                    />
                  </FormGroup>
                </Col>
              </Row>

              <Row>
                <Col md="12">
                  <FormGroup>
                    <ButtonGroup>
                      <Button
                        outline={this.state.formData.isPrivate}
                        onClick={() => this.onChangeField("isPrivate", false)}
                      >
                        Public
                      </Button>
                      <Button
                        outline={!this.state.formData.isPrivate}
                        onClick={() => this.onChangeField("isPrivate", true)}
                      >
                        Private
                      </Button>
                    </ButtonGroup>
                  </FormGroup>
                </Col>
              </Row>
            </div>
          </Form>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" outline onClick={this.props.onClose}>
            Cancel
          </Button>

          <Button
            id="Popover1"
            color="danger"
            outline
            onClick={this.toggleDelete}
          >
            Delete
          </Button>

          <Popover
            placement="top"
            isOpen={this.state.popoverOpen}
            target="Popover1"
            toggle={this.toggleDelete}
          >
            <PopoverHeader>Are you sure you want to delete?</PopoverHeader>
            <PopoverBody>
              <Button color="danger" outline onClick={this.onDelete}>
                Confirm
              </Button>
            </PopoverBody>
          </Popover>

          <Button color="success" outline onClick={() => this.onSubmit(false)}>
            Update
          </Button>
          <Button color="success" outline onClick={() => this.onSubmit(true)}>
            Update &amp; Next
          </Button>
        </ModalFooter>
      </Modal>
    );
  }
}

PhotoForm.propTypes = {
  photoId: PropTypes.string.isRequired,
  type: PropTypes.string.isRequired,
  visible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onDone: PropTypes.func.isRequired,
  onDoneNext: PropTypes.func.isRequired
};

export default compose(
  connect(
    state => ({
      photo: state.gallery.detail,
      categories: state.categories.list
    }),
    dispatch => bindActionCreators({ ...actions }, dispatch)
  ),
  withRouter
)(PhotoForm);

const PhotoImage = styled.View`
  width: 100%;
  height: 400px;
  background-position: center;
  background-repeat: no-repeat;
  background-size: contain;
`;
